// src/services/api.ts - Centralized API service
import { User, WatchedUsername, Platform } from '../types';

class APIService {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409 && errorData.error === 'account_deletion_requested') {
          const error = new Error('Account deletion requested'); error.name = 'AccountDeletionRequested'; throw error;
        }
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  // Profile methods (auth is now handled by Clerk)
  async getProfile(): Promise<User> {
    return this.request('/profile');
  }

  // Username methods
  async getUsernames(): Promise<WatchedUsername[]> {
    return this.request('/usernames');
  }

  async addUsername(username: string, platform_id: string, priority: number = 1): Promise<WatchedUsername> {
    return this.request('/usernames', {
      method: 'POST',
      body: JSON.stringify({ username, platform_id, priority }),
    });
  }

  async deleteUsername(watch_id: string): Promise<void> {
    return this.request(`/usernames/${watch_id}`, {
      method: 'DELETE',
    });
  }

  async updateUsername(watch_id: string, updates: Partial<WatchedUsername>): Promise<WatchedUsername> {
    return this.request(`/usernames/${watch_id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Platform methods
  async getPlatforms(): Promise<Platform[]> {
    return this.request('/platforms');
  }

  // Notification methods
  async updateNotificationPreferences(preferences: any): Promise<void> {
    return this.request('/profile/notifications', {
      method: 'PATCH',
      body: JSON.stringify(preferences),
    });
  }

  // Admin methods
  async checkAdminStatus(signal?: AbortSignal): Promise<{ isAdmin: boolean; userId?: string }> {
    return this.request('/api/admin/check', { signal });
  }

  async getAdminStats(signal?: AbortSignal): Promise<AdminStats> {
    return this.request('/api/admin/stats', { signal });
  }

  async getAdminActivity(signal?: AbortSignal): Promise<AdminActivity> {
    return this.request('/api/admin/activity', { signal });
  }
}

// Admin types
export interface AdminStats {
  monitoring?: {
    stale: boolean; backlog: number; oldest_due_at?: number | null; awaiting_recheck: number;
    last_run?: { started_at?: number; finished_at?: number; outcome: string; attempted?: number; successful?: number; unknown_count?: number; error_count?: number };
    platforms: Array<{ platform_id: string; platform_name: string; checking_enabled: number; paused_reason?: string; consecutive_failed_runs: number; cooldown_until?: number | null }>;
    notifications: Array<{ status: string; count: number }>;
  };
  users: {
    total_users: number;
    active_users: number;
    new_users_7d: number;
    new_users_30d: number;
  };
  subscriptions: Array<{
    plan_type: string;
    count: number;
  }>;
  platforms: Array<{
    platform_name: string;
    platform_id: string;
    total_watches: number;
    available_count: number;
    unavailable_count: number;
  }>;
  watches: {
    total_watches: number;
    available: number;
    unavailable: number;
    unique_combinations: number;
  };
  checks_by_day: Array<{
    check_date: string;
    checks_count: number;
    found_available: number;
  }>;
  notifications: {
    total_notifications: number;
    sent: number;
    pending: number;
    failed: number;
  };
}

export interface AdminActivity {
  recent_users: Array<{
    user_id: string;
    email: string;
    created_at: string;
  }>;
  recent_finds: Array<{
    username: string;
    platform_name: string;
    status: string;
    last_checked: string;
    user_email: string;
  }>;
  recent_notifications: Array<{
    notification_type: string;
    status: string;
    sent_at: string;
    email: string;
    username: string;
    platform_name: string;
  }>;
}

export const apiService = new APIService(
  process.env.NODE_ENV === 'development' 
    ? 'http://localhost:8787' 
    : 'https://api.namecamper.com'
);
